import React, { useState, useRef, useEffect } from 'react';
import api from '../../services/api';

interface Message {
  role: 'user' | 'bot';
  text: string;
  sources?: Array<{ pdf: string; page: number }>;
  timestamp: Date;
}

const INITIAL_MESSAGE: Message = {
  role: 'bot',
  text: 'Hi! Ask me anything about biodiversity, species, or our research documents.',
  timestamp: new Date(),
};

const ChatbotWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([INITIAL_MESSAGE]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom whenever messages update
  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // Focus input when widget opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    setMessages((prev) => [...prev, { role: 'user', text, timestamp: new Date() }]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/chatbot/query', { question: text });
      const { response: botText, sources } = res.data;
      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: botText, sources, timestamp: new Date() },
      ]);
    } catch (err: any) {
      const errMsg =
        err?.response?.data?.detail || 'Something went wrong. Please try again.';
      setMessages((prev) => [
        ...prev,
        { role: 'bot', text: errMsg, timestamp: new Date() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="chatbot-widget" aria-live="polite">
      {/* Chat panel */}
      {open && (
        <div className="chatbot-widget-panel" role="dialog" aria-label="EcoPredict chatbot">
          {/* Header */}
          <div className="chatbot-widget-header">
            <span className="chatbot-widget-title">🤖 ECOVYA</span>
            <button
              type="button"
              className="chatbot-widget-close"
              aria-label="Close chatbot"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-widget-messages" role="log" aria-label="Chat messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`chatbot-widget-msg ${msg.role}`}>
                <div className="chatbot-widget-bubble">
                  <p>{msg.text}</p>
                  {msg.sources && msg.sources.length > 0 && (
                    <ul className="chatbot-widget-sources">
                      {msg.sources.slice(0, 3).map((s, si) => (
                        <li key={si}>
                          📄 {s.pdf} — p.{s.page}
                        </li>
                      ))}
                    </ul>
                  )}
                  <span className="chatbot-widget-time">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
            {loading && (
              <div className="chatbot-widget-msg bot">
                <div className="chatbot-widget-bubble loading">
                  <span className="chatbot-widget-dots">
                    <span />
                    <span />
                    <span />
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form className="chatbot-widget-form" onSubmit={sendMessage}>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question..."
              disabled={loading}
              aria-label="Chat input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              aria-label="Send message"
            >
              ➤
            </button>
          </form>
        </div>
      )}

      {/* Toggle bubble */}
      <button
        type="button"
        className={`chatbot-widget-fab ${open ? 'open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close chatbot' : 'Open chatbot'}
        aria-expanded={open}
      >
        {open ? '✕' : '🤖'}
      </button>
    </div>
  );
};

export default ChatbotWidget;
